import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { CheckCircle, Phone, Building, Sparkles, Mail } from 'lucide-react';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  needsPhone: boolean;
  needsOrganizationName: boolean;
  needsEmail: boolean;
}

export const OnboardingModal: React.FC<OnboardingModalProps> = ({
  isOpen,
  onClose,
  needsPhone,
  needsOrganizationName,
  needsEmail
}) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [phone, setPhone] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = [
    {
      id: 'welcome',
      title: 'Welcome to The PEO App!',
      icon: <Sparkles className="h-8 w-8 text-primary" />,
      content: (
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">
            Let's get you set up with a few quick details to personalize your experience.
          </p>
          <p className="text-sm text-muted-foreground">
            This will only take a minute and will help us serve you better.
          </p>
        </div>
      )
    },
    ...(needsOrganizationName ? [{
      id: 'organization',
      title: 'Your Organization',
      icon: <Building className="h-8 w-8 text-primary" />,
      content: (
        <div className="space-y-4">
          <div>
            <Label htmlFor="organizationName">Organization Name</Label>
            <Input
              id="organizationName"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              placeholder="Your company or organization name"
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              This helps us customize your experience and match you with relevant PEO providers.
            </p>
          </div>
        </div>
      )
    }] : []),
    ...(needsEmail ? [{
      id: 'email',
      title: 'Email Address',
      icon: <Mail className="h-8 w-8 text-primary" />,
      content: (
        <div className="space-y-4">
          <div>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@company.com"
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              We'll use this to send you important updates and match you with PEO providers.
            </p>
          </div>
        </div>
      )
    }] : []),
    ...(needsPhone ? [{
      id: 'phone',
      title: 'Contact Information',
      icon: <Phone className="h-8 w-8 text-primary" />,
      content: (
        <div className="space-y-4">
          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              This allows PEO providers to contact you directly about quotes and questions.
            </p>
          </div>
        </div>
      )
    }] : []),
    {
      id: 'complete',
      title: 'You\'re All Set!',
      icon: <CheckCircle className="h-8 w-8 text-green-600" />,
      content: (
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">
            Great! Your profile is now complete. You can start exploring PEO options and create your first application.
          </p>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-green-800">
              <strong>Next steps:</strong> Start by creating an application to get matched with PEO providers that fit your needs.
            </p>
          </div>
        </div>
      )
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = async () => {
    if (isLastStep) {
      await handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleComplete = async () => {
    if (!user) return;

    setIsSubmitting(true);
    try {
      const updates: any = {
        onboarding_completed: true,
        onboarding_step: steps.length,
      };

      if (needsOrganizationName && organizationName) {
        updates.organization_name = organizationName;
      }

      if (needsPhone && phone) {
        updates.phone = phone;
      }

      if (needsEmail && email) {
        updates.email = email;
      }

      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Profile completed successfully!');
      onClose();
    } catch (error: any) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ 
          onboarding_skipped: true,
          onboarding_completed: true 
        })
        .eq('id', user.id);

      if (error) throw error;

      toast.success('Onboarding skipped. You can complete your profile later from settings.');
      onClose();
    } catch (error: any) {
      console.error('Error skipping onboarding:', error);
      toast.error('Failed to skip onboarding. Please try again.');
    }
  };

  const canProceed = () => {
    const step = currentStepData.id;
    if (step === 'organization' && needsOrganizationName) {
      return organizationName.trim().length > 0;
    }
    if (step === 'phone' && needsPhone) {
      return phone.trim().length > 0;
    }
    if (step === 'email' && needsEmail) {
      return email.trim().length > 0 && email.includes('@');
    }
    return true;
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center space-x-3 mb-4">
            {currentStepData.icon}
            <DialogTitle className="text-xl">{currentStepData.title}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Progress indicator */}
          <div className="flex space-x-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 flex-1 rounded-full ${
                  index <= currentStep ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>

          {/* Step content */}
          <div className="min-h-[120px]">
            {currentStepData.content}
          </div>

          {/* Actions */}
          <div className="flex justify-between">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-muted-foreground"
            >
              Skip for now
            </Button>

            <div className="space-x-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(prev => prev - 1)}
                >
                  Back
                </Button>
              )}
              <Button
                onClick={handleNext}
                disabled={!canProceed() || isSubmitting}
              >
                {isSubmitting ? 'Saving...' : isLastStep ? 'Complete Setup' : 'Next'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};